/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Tabs, Select, Checkbox, InputNumber, InputRef, DatePicker, TimePicker } from 'antd';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import {
  EditOutlined,
  ShoppingCartOutlined,
  ProfileOutlined,
  VideoCameraOutlined,
  DollarOutlined,
  TableOutlined,
} from '@ant-design/icons';
import {
  showProduct,
  editProduct,
  createProduct,
  storeProducts,
  updateProduct,
  ProductRequest,
  ProductApiData,
  StatusOption,
  TypeOption,
  UnitOption,
  PricingOption,
  AmenityOption,
} from 'api/product.api';
import { BaseButtonsForm } from '@app/components/common/forms/BaseButtonsForm/BaseButtonsForm';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { Space } from 'antd';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { useNavigate } from 'react-router-dom';
import { BaseSelect } from '@app/components/common/selects/BaseSelect/BaseSelect';
import { BaseImage } from '@app/components/common/BaseImage/BaseImage';
import { CategoryOption } from '@app/api/product.api';
import { Tooltip } from 'antd';
import { MdModeEdit } from 'react-icons/md';
import { MeetingTableRow } from '@app/api/meeting.api';
import { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import { BaseTypography } from '@app/components/common/BaseTypography/BaseTypography';
import { PricingOptionsSection } from './PricingOptionsSection';
import { IoEye } from 'react-icons/io5';
import ZoomLinks from './ZoomLinks';
import { FileInfo } from '@app/api/assets.api';
import { Assets } from '@app/components/assets/Assets';
import ReactQuill from 'react-quill';
import useApiErrorHandler from '@app/components/Error/ErrorMsgHandling';

interface TypeOfForm {
  type: 'edit' | 'create' | 'view';
}

interface ProductFormProps extends TypeOfForm {
  editable: boolean;
  id?: string;
}

const { TextArea } = Input;
const { TabPane } = Tabs;
const { Option } = Select;

const formItemLayout = {
  labelCol: { span: 24 },
  wrapperCol: { span: 24 },
};

export const ProductForm: React.FC<ProductFormProps> = ({ type, id, editable = false }) => {
  const [isFieldsChanged, setFieldsChanged] = useState(false);
  const [form] = BaseButtonsForm.useForm();
  const productId = id ? parseInt(id) : null;
  const { t } = useTranslation();
  const [isLoading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [imageList, setImageList] = useState<Array<{ key: string; image: any }>>([]);
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);
  const [typeOptions, setTypeOptions] = useState<TypeOption[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([]);
  const [durationUnitOptions, setDurationUnitOptions] = useState<UnitOption[]>([]);
 const [sku, setSku] = useState<string|null>(null);
 const [quantity, setQuantity] = useState<number>(1);
  const [leadTimeUnitOptions, setLeadTimeUnitOptions] = useState<UnitOption[]>([]);
  const [amenitiesOptions, setAmenitiesOptions] = useState<AmenityOption[]>([]);
  const [amenities, setAmenities] = useState<string[]>([]);
  const [pricingOptionMeasures, setPricingOptionMeasures] = useState<UnitOption[]>([]);
  const [productType, setProductType] = useState('SI');
  const calendarColor = useRef<InputRef>(null);
  const [labelInsteadPrice, setLabelInsteadPrice] = useState<boolean | null>(null);
  const [selectedImage, setSelectedImage] = useState<string>('error');
  const [imageUrl, setImageUrl] = useState<string>('');
  const [slug, setSlug] = useState<string>('');
  const nameInputRef = useRef<InputRef>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [showZoomLinkTab, setShowZoomLinkTab] = useState(false);
  const [showQuantityLabel, setShowQuantityLabel] = useState(false);
  const [showPricingOptionsTab, setShowPricingOptionsTab] = useState(false);
  const [allZoomLinks, setAllZoomLinks] = useState<MeetingTableRow[]>([]);
  const [selectedZoomLinks, setSelectedZoomLinks] = useState<MeetingTableRow[]>([]);
  const [productPricingOptions, setProductPricingOptions] = useState<PricingOption[]>([]);
  const [productImages, setProductImages] = useState<FileInfo[]>([]);
  const [pageTitle, setPageTitle] = useState<string>('');
  const [editorHtml, setEditorHtml] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [showAmenities, setShowAmenities] = useState(true);

  const toolbarOptions = [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    ['blockquote'],
    ['link'],
    [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
    [{ script: 'sub' }, { script: 'super' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ direction: 'rtl' }],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    ['clean'],
  ];

  const handleErrorMessages = useApiErrorHandler();

  const handleNameChange = (e: any) => {
    const a = nameInputRef?.current?.input?.value || '';
    const b = a.replace(/\s+/g, ' ').trim().replaceAll(' ', '_').toLowerCase();
    form.setFieldValue('slug', b);
  };

  const getPopupContainer = (triggerNode: HTMLElement | null) => {
    return triggerNode || document.body;
  };

  const handleLabelInsteadPriceChange = (e: any) => {
    setLabelInsteadPrice(e.target.checked);
  };

  const handleSubmit = (values: ProductRequest) => {
    setLoading(true);

    values.image = imageUrl || '';
    if (labelInsteadPrice === false) {
      values.price_label = null;
    }
    values.images = productImages?.filter((e)=>e.action != '2').map(e => e.action == '1' ? ({name:e.name,action:e.action}):({id:e.id,name:e.name,action:e.action})) || [];
    values.show_zoom_links = showZoomLinkTab;
    values.show_pricing_options = showPricingOptionsTab;
    values.meetings = values.show_zoom_links && values.meetings ? values.meetings : [];
    values.description = values.description || null;
    values.details = editorHtml;
    values.pricing_options = productPricingOptions;
    values.from_date = dayjs(values.from_date).isValid() ? dayjs(values.from_date).format('YYYY-MM-DD') : null;
    values.to_date = dayjs(values.to_date).isValid() ? dayjs(values.to_date).format('YYYY-MM-DD') : null;
    //  values.to_date !== undefined || values.to_date !== null ? dayjs(values.to_date).format('YYYY-MM-DD') : null;
    values.from_time = dayjs(values.from_time).isValid() ? dayjs(values.from_time).format('HH:mm:ss') : null;
    //values.from_time !== undefined || values.from_time !== null ? dayjs(values.from_time).format('HH:mm:ss') : null;
    values.to_time = dayjs(values.to_time).isValid() ? dayjs(values.to_time).format('HH:mm:ss') : null;
    //values.to_time !== undefined || values.to_time !== null ? dayjs(values.to_time).format('HH:mm:ss') : null;
    values.show_quantity_field = showQuantityLabel;
    values.quantity_label = values.quantity_label || '';
    values.amenities = amenities;
    // values.sku = sku;
    // values.quantity = quantity;
    values.start_time_interval = values.start_time_interval || 60;

    if (editable) {
      if (type === 'create') {
        storeProducts(values)
          .then((res: any) => {
            setLoading(false);
            console.log(res);
            navigate(`/view-product/${res?.data?.id}`);
          })
          .catch((err: any) => {
            setLoading(false);
            handleErrorMessages(err);
            console.error(err);
          });
      } else if (type === 'edit' && productId) {
        updateProduct(productId, values)
          .then(() => {
            setLoading(false);
            navigate(`/view-product/${id}`);
          })
          .catch((err: any) => {
            setLoading(false);
            handleErrorMessages(err);
            console.error(err);
          });
      }
    }
  };

  const handleTypeChange = (e: any) => {
    setProductType(e);
    setShowAmenities(e === 'AP');
  };

  const handleCategoryChange = (value: unknown) => {
    const categoryId = value as number;
    setSelectedCategory(categoryId);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setImageFile(file);

    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setSelectedImage(event.target?.result as string);
        setImageUrl(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditIconClick = () => {
    navigate(`/edit-product/${id}`);
  };

  const handleViewIconClick = () => {
    navigate(`/view-product/${id}`);
  };

  const setApiData = (res: ProductApiData) => {
    console.log(res);
    setEditorHtml(res?.product?.details || '');
    setStatusOptions(res?.status_options);
    setTypeOptions(res?.type_options);
    setImageUrl(res?.product?.image || '');
    setSelectedImage(res?.product?.image_url || 'error');
    setDurationUnitOptions(res?.duration_unit_options);
    //setSku(res?.product?.sku || null)
    //setQuantity(res?.product?.quantity || 1)
    setLeadTimeUnitOptions(res?.lead_time_unit_options);
    setCategoryOptions(res?.category_options);
    setProductType(res?.product?.type);
    //setLabelInsteadPrice(res?.product?.appointable?.label_instead_price);
    setSlug(res?.product?.slug);
    setAllZoomLinks(res?.zoom_links);
    setShowZoomLinkTab(res?.product?.show_zoom_links || false);
    setShowPricingOptionsTab(res?.product?.show_pricing_options || false);
    setPricingOptionMeasures(res?.pricing_option_measures);
    setShowQuantityLabel(res?.product?.show_quantity_field || false);
    setAmenitiesOptions(res?.amenities_options);
    setShowAmenities(res?.product?.type === 'AP');
    setAmenities(res?.product?.amenities || null);
    const options: PricingOption[] = res?.product?.pricing_options?.map((e: any) => {
      return { ...e, action: '3' };
    });
    setProductPricingOptions(options);
    setSelectedZoomLinks(res?.product?.meetings || []);
    setProductImages(res?.product?.images ? res?.product?.images: []);

    form.setFieldsValue({
      name: res?.product?.name,
      sku: res?.product?.sku,
      slug: res?.product?.slug,
      quantity: res?.product?.quantity || 1,
      details: res?.product?.details,
      description: res?.product?.description,
      regular_price: res?.product?.regular_price,
      category_id: res?.product?.product_category_id,
      sale_price: res?.product?.sale_price,
      status: res?.product?.status,
      type: res?.product?.type,
      label_instead_price: res?.product?.appointable?.label_instead_price || false,
      price_label: res?.product?.appointable?.price_label || null,
      interval: res?.product?.appointable?.interval || 0,
      lead_time: res?.product?.appointable?.lead_time || 0,
      requires_confirmation: res?.product?.appointable?.requires_confirmation || false,
      calendar_color: res?.product?.appointable?.calendar_color || '#0073aa',
      meetings: res?.product?.meetings?.map(meeting => meeting.id) || [],
      show_zoom_links: res?.product?.show_zoom_links || false,
      show_pricing_options: res?.product?.show_pricing_options || false,
      pricing_options: productPricingOptions,
      from_date: res?.product?.appointable?.from_date ? dayjs(res?.product?.appointable?.from_date) : null,
      to_date: res?.product?.appointable?.to_date ? dayjs(res?.product?.appointable?.to_date) : null,
      from_time: res?.product?.appointable?.from_time ? dayjs(res?.product?.appointable?.from_time, 'HH:mm:ss') : null,
      to_time: res?.product?.appointable?.to_time ? dayjs(res?.product?.appointable?.to_time, 'HH:mm:ss') : null,
      min_reserve: res?.product?.appointable?.min_reserve || 0,
      max_reserve: res?.product?.appointable?.max_reserve || 0,
      reserve_measure: res?.product?.appointable?.reserve_measure,
      lead_time_measure: res?.product?.appointable?.lead_time_measure,
      start_time_interval: res?.product?.appointable?.start_time_interval || 0,
      quantity_label: res?.product?.quantity_label,
      show_quantity_field: res?.product?.show_quantity_field || false,
      amenities: res?.product?.amenities,
    });
    // if (type !== 'create') {
    //   const values = allZoomLinks.filter((e) => form.getFieldValue('meetings').includes(e.id));
    //   setSelectedZoomLinks(values);
    // }
  };

  const handleCalendarColor = (e: any) => {
    form.setFieldValue('calendar_color', calendarColor?.current?.input?.value || '#0073aa');
  };

  const handleImageUpload = (info: any) => {
    const fileList = [...info.fileList];

    const promises = fileList.map((file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
          resolve({
            name: file.name,
            key: file.uid,
            image: URL.createObjectURL(file.originFileObj),
            image64: e.target?.result,
          });
        };

        reader.onerror = (err) => {
          reject(err);
        };

        reader.readAsDataURL(file.originFileObj);
      });
    });

    Promise.all(promises)
      .then((base64Results: any) => {
        console.log('Newly uploaded images:', base64Results);
        setImageList(base64Results);
      })
      .catch((error) => {
        console.error('Error converting images to base64:', error);
      });
  };

  const handleRemoveImage = (key: string) => {
    const updatedList = imageList.filter((item) => item.key !== key);
    console.log('Updated Image List:', updatedList);
    setImageList(updatedList);
  };

  useEffect(() => {
    // Function to fetch data from the API
    setLoading(true);
    if (productId) {
      if (type === 'view') {
        showProduct(productId)
          .then((res: any) => {
            setApiData(res);
            setPageTitle(`${res?.product?.name}`);
            setLoading(false);
          })
          .catch((error) => handleErrorMessages(error));
      } else if (type === 'edit') {
        editProduct(productId)
          .then((res: any) => {
            setApiData(res);
            setPageTitle(`${res?.product?.name}`);
            setLoading(false);
          })
          .catch((error) => handleErrorMessages(error));
      }
    } else {
      createProduct()
        .then((res: any) => {
          setApiData(res);
          setPageTitle(`New`);
          setLoading(false);
        })
        .catch((error) => handleErrorMessages(error));
    }
    // Set showAmenities based on initial product type
    setShowAmenities(productType === 'AP');
  }, []); // The empty dependency array means this effect will only run once, equivalent to componentDidMount

  const columns: ColumnsType<MeetingTableRow> = [
    {
      title: 'Meeting ID',
      dataIndex: 'meeting_id',
      key: 'meeting_id',
    },
    {
      title: 'Topic',
      dataIndex: 'topic',
      key: 'topic',
    },
    {
      title: 'Date & Time',
      dataIndex: 'start_time',
      render(value, record, index) {
        return <div>{dayjs(record.start_time).format('MMMM D, YYYY  HH:mm a')}</div>;
      },
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      render: (value, record, index) => `${record.duration} minutes`,
    },

    {
      title: 'Password',
      dataIndex: 'password',
      key: 'password',
    },
    {
      title: 'Join URL',
      dataIndex: 'join_url',
      key: 'join_url',
    },
  ];

  return (
    <>
      <BaseCard
        id="validation form"
        title={`Product - ` + pageTitle || 'New'}
        padding="1.25rem"
        style={{ margin: '20px', marginTop: '0px', width: '98%' }}
        extra={
          <>
            {(type === 'view' && (
              <>
                <Space>
                  <Tooltip title={t('common.edit')}>
                    <BaseButton type="text" onClick={() => handleEditIconClick()} icon={<MdModeEdit />} />
                  </Tooltip>
                </Space>
              </>
            )) ||
              (type === 'edit' && (
                <>
                  <Space>
                    <Tooltip title={t('view')}>
                      <BaseButton type="text" onClick={() => handleViewIconClick()} icon={<IoEye />} />
                    </Tooltip>
                  </Space>
                </>
              ))}
            <Space>
              <Tooltip title={'View all'}>
                <BaseButton type="text" onClick={() => navigate('/all-products')} icon={<TableOutlined />} />
              </Tooltip>
            </Space>
          </>
        }
      >
        <BaseButtonsForm
          {...formItemLayout}
          name="productsForm"
          onFinish={handleSubmit}
          form={form}
          loading={isLoading}
          isFieldsChanged={isFieldsChanged}
          setFieldsChanged={setFieldsChanged}

          // onFieldsChange={() => {formData}}
          // initialValues={formData}
        >
          {/* {type === 'view' && showEditIcon && (
            <Tooltip title={t('common.edit')}>
              <MdModeEdit onClick={handleEditIconClick} />
            </Tooltip>
          )} */}
          <BaseRow gutter={[16, 16]}>
            <BaseCol xs={24} md={8} lg={12}>
              <BaseButtonsForm.Item
                name="name"
                label="Product Name"
                key="name"
                rules={[{ required: true, message: 'Name is required' }]}
              >
                <BaseInput
                  ref={nameInputRef}
                  placeholder="Enter Product Name"
                  disabled={!editable}
                  onChange={handleNameChange}
                />
              </BaseButtonsForm.Item>
            </BaseCol>
          </BaseRow>

          <BaseRow gutter={[16, 16]}>
            <BaseCol xs={24} md={8} lg={12}>
              <BaseButtonsForm.Item
                name="slug"
                label="Slug"
                key="slug"
                rules={[{ required: true, message: 'Slug is required' }]}
              >
                <BaseInput placeholder="Enter Slug" disabled={!editable} value={slug} />
              </BaseButtonsForm.Item>
            </BaseCol>
          </BaseRow>

          <BaseRow gutter={[16, 16]}>
            <BaseCol xs={24} md={8} lg={12}>
              <BaseButtonsForm.Item
                name="type"
                label="Product Type"
                rules={[{ required: true, message: 'Product Type is required' }]}
              >
                <BaseSelect
                  placeholder="Select product type"
                  disabled={!editable}
                  //value={productType}
                  onChange={handleTypeChange}
                >
                  {typeOptions?.map((option) => (
                    <Option key={option.key} value={option.key}>
                      {option.name}
                    </Option>
                  ))}
                </BaseSelect>
              </BaseButtonsForm.Item>

              {/* <p style={{ fontSize: '12px', color: '#888' }}>
              The description is not prominent by default; however, some themes may show it.
            </p> */}
            </BaseCol>
          </BaseRow>

          <BaseRow gutter={[16, 16]}>
            <BaseCol xs={24} md={8} lg={12}>
              <BaseButtonsForm.Item
                name="category_id"
                label="Product Category"
                rules={[{ required: true, message: 'Product Category is required' }]}
              >
                <BaseSelect placeholder="Select product category" disabled={!editable} onChange={handleCategoryChange}>
                  {categoryOptions?.map((option) => (
                    <Option key={option.category_chain} value={option.id}>
                      {option.category_chain}
                    </Option>
                  ))}
                </BaseSelect>
              </BaseButtonsForm.Item>
            </BaseCol>
          </BaseRow>

          <BaseRow gutter={[16, 16]}>
            <BaseCol xs={24} md={24} lg={24}>
              <BaseButtonsForm.Item name="details" label="Product Details" key="details">
                <ReactQuill
                  theme="snow"
                  modules={{ toolbar: toolbarOptions }}
                  value={editorHtml}
                  onChange={setEditorHtml}
                  readOnly={!editable}
                />
              </BaseButtonsForm.Item>
            </BaseCol>
            {showAmenities && (
              <BaseCol span={24}>
                <BaseButtonsForm.Item name="amenities" key="amenities" label="Amenities" initialValue={[]}>
                  <Select
                    placeholder="Select Amenities"
                    mode="multiple"
                    value={amenities}
                    disabled={!editable}
                    onChange={(value: any) => {
                      setAmenities(value);
                    }}
                  >
                    {amenitiesOptions?.map((option) => (
                      <Option key={option.name} value={option.key}>
                        {option.name}
                      </Option>
                    ))}
                  </Select>
                </BaseButtonsForm.Item>
              </BaseCol>
            )}
          </BaseRow>

          <BaseRow gutter={[16, 16]}>
            <BaseCol xs={24} md={8} lg={12}>
              <BaseButtonsForm.Item name="image" label="Image">
                <Space direction="vertical">
                  <input type="file" accept="image/*" onChange={handleFileChange} hidden={!editable} />
                  <BaseImage
                    src={selectedImage}
                    style={{ width: '200px', height: '200px' }}
                    preview={false}
                    fallback="data:image/png;base64,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"
                  />
                </Space>
              </BaseButtonsForm.Item>
              <p style={{ fontStyle: 'italic', fontSize: '14px' }}>
                *Please make sure to upload an image with 450 x 450 dimensions
              </p>
            </BaseCol>
          </BaseRow>

          <BaseRow gutter={[0, 0]} align={'middle'}>
            <BaseCol span={24}>
              <BaseButtonsForm.Item name="show_zoom_links" key="show_zoom_links" fieldId="show_zoom_links">
                <Checkbox
                  checked={showZoomLinkTab}
                  onChange={(e: any) => {
                    setShowZoomLinkTab(e.target.checked);
                  }}
                  disabled={!editable}
                >
                  <BaseTypography.Text>Enable zoom links</BaseTypography.Text>
                </Checkbox>
              </BaseButtonsForm.Item>
            </BaseCol>
            {productType === 'AP' && (
              <BaseCol span={24}>
                <BaseButtonsForm.Item
                  name="show_pricing_options"
                  key="show_pricing_options"
                  fieldId="show_pricing_options"
                >
                  <Checkbox
                    checked={showPricingOptionsTab}
                    onChange={(e: any) => {
                      setShowPricingOptionsTab(e.target.checked);
                    }}
                    disabled={!editable}
                  >
                    <BaseTypography.Text>Enable pricing options</BaseTypography.Text>
                  </Checkbox>
                </BaseButtonsForm.Item>
              </BaseCol>
            )}
          </BaseRow>
          <br></br>
          <Tabs defaultActiveKey="1">
            <TabPane
              tab={
                <span>
                  <EditOutlined />
                  {t('General')}
                </span>
              }
              key="1"
            >
              <br></br>

              {/* Form for Tab 1 */}

              <BaseRow gutter={[16, 16]}>
                <BaseCol xs={24} md={12}>
                  <BaseButtonsForm.Item
                    name="regular_price"
                    label="Regular Price (Rs)"
                    key="regular_price"
                    rules={[{ required: true, message: 'Regular Price is required' }]}
                  >
                    <BaseInput placeholder="Enter Regular Price" disabled={!editable} />
                  </BaseButtonsForm.Item>
                </BaseCol>
                <BaseCol xs={24} md={12}>
                  <BaseButtonsForm.Item
                    name="sale_price"
                    label="Sale Price (Rs)"
                    key="sale_price"
                    dependencies={['regular_price']}
                    rules={[
                      { required: true, message: 'Sale Price is required' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const regularPrice = parseFloat(getFieldValue('regular_price'));
                          const salePrice = parseFloat(value);

                          if (!value || salePrice <= regularPrice) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('Sale Price must be equal or less than the Regular Price'));
                        },
                      }),
                    ]}
                  >
                    <BaseInput placeholder="Enter Sale Price" disabled={!editable} />
                  </BaseButtonsForm.Item>
                </BaseCol>
              </BaseRow>
              <p style={{ fontStyle: 'italic', fontSize: '14px' }}>
                *Please make sure the Sale Price is equal or less than the Regular Price
              </p>

              {productType === 'AP' && (
                <>
                  <BaseRow gutter={[16, 16]}>
                    <BaseCol xs={24} md={24}>
                      <BaseButtonsForm.Item
                        name="label_instead_price"
                        valuePropName="checked"
                        label="Label instead of price?"
                      >
                        <Checkbox
                          onChange={handleLabelInsteadPriceChange}
                          checked={!labelInsteadPrice}
                          disabled={!editable}
                        >
                          <p style={{ fontSize: 13 }}>
                            Check this box if the appointment should display text label instead of fixed price amount.
                          </p>
                        </Checkbox>
                      </BaseButtonsForm.Item>
                    </BaseCol>
                  </BaseRow>
                  {labelInsteadPrice == true && (
                    <>
                      <BaseRow gutter={[16, 16]}>
                        <BaseCol xs={24} md={8} lg={12}>
                          <BaseButtonsForm.Item name="price_label" label="Price Label" key="price_label">
                            <BaseInput type="text" placeholder="Enter Price Label" disabled={!editable} />
                          </BaseButtonsForm.Item>
                        </BaseCol>
                      </BaseRow>
                      <br></br>
                    </>
                  )}
                  <BaseRow gutter={[16, 8]}>
                    <BaseCol xs={24} md={12}>
                      <Space>
                        <BaseButtonsForm.Item
                          initialValue={0}
                          name="min_reserve"
                          label="Duration(Min)"
                          rules={[{ required: productType === 'AP', message: 'Min Duration is required' }]}
                        >
                          <InputNumber disabled={!editable} placeholder="min" />
                        </BaseButtonsForm.Item>
                        <BaseButtonsForm.Item name="max_reserve" label="Duration(Max)" initialValue={0}>
                          <InputNumber disabled={!editable} placeholder="max" />
                        </BaseButtonsForm.Item>

                        <BaseButtonsForm.Item
                          name="reserve_measure"
                          label=" "
                          initialValue="minutes"
                          tooltip="How long do you plan this appointment to last?"
                          rules={[{ required: productType === 'AP', message: 'Duration unit is required' }]}
                        >
                          <Select
                            placeholder="Select Duration Unit"
                            getPopupContainer={getPopupContainer}
                            disabled={!editable}
                          >
                            {durationUnitOptions?.map((option) => (
                              <Option key={option.name} value={option.key}>
                                {option.name}
                              </Option>
                            ))}
                          </Select>
                        </BaseButtonsForm.Item>
                      </Space>
                      {/* <BaseRow gutter={[16, 0]}>
                        <BaseCol xs={24} sm={12} lg={6}>
                          <BaseButtonsForm.Item
                            name="min_reserve"
                            label="Min Duration"
                            rules={[{ required: productType === 'AP', message: 'Min Duration is required' }]}
                          >
                            <InputNumber disabled={!editable}/>
                          </BaseButtonsForm.Item>
                        </BaseCol>
                        <BaseCol xs={24} sm={12} lg={6}>
                          <BaseButtonsForm.Item
                            name="max_reserve"
                            label="Max Duration"
                          >
                            <InputNumber disabled={!editable}/>
                          </BaseButtonsForm.Item>
                        </BaseCol>
                        <BaseCol xs={24} sm={12} lg={6}>
                          <BaseButtonsForm.Item
                            name="duration_unit"
                            label=" "
                            tooltip="How long do you plan this appointment to last?"
                            rules={[{ required: productType === 'AP', message: 'Duration unit is required' }]}
                          >
                            <Select placeholder="Select Duration Unit" getPopupContainer={getPopupContainer} disabled={!editable}>
                              {durationUnitOptions?.map((option) => (
                                <Option key={option.name} value={option.key}>
                                  {option.name}
                                </Option>
                              ))}
                            </Select>
                          </BaseButtonsForm.Item>
                        </BaseCol>
                      </BaseRow> */}
                    </BaseCol>

                    <BaseCol xs={24} md={12}>
                      <BaseRow gutter={[8, 8]}>
                        <BaseCol xs={24} sm={12} lg={6}>
                          <BaseButtonsForm.Item
                            initialValue={0}
                            name="interval"
                            label="Interval (in minutes)"
                            rules={[{ required: productType === 'AP', message: 'Interval is required' }]}
                          >
                            <InputNumber disabled={!editable} />
                          </BaseButtonsForm.Item>
                        </BaseCol>
                        {/* <BaseCol xs={24} sm={12} lg={6}>
                          <BaseButtonsForm.Item
                            name="interval_unit"
                            label=" "
                            initialValue="minutes"
                            tooltip="Select intervals when each appointment slot is available for scheduling."
                            rules={[{ required: productType === 'AP', message: 'Interval unit is required' }]}
                          >
                            <Select placeholder="Select Interval Unit" getPopupContainer={getPopupContainer}disabled={!editable}>
                              {intervalUnitOptions?.map((option) => (
                                <Option key={option.name} value={option.key}>
                                  {option.name}
                                </Option>
                              ))}
                            </Select>
                          </BaseButtonsForm.Item>
                        </BaseCol> */}
                      </BaseRow>
                    </BaseCol>

                    <BaseCol xs={24} md={12}>
                      <BaseRow gutter={[16, 0]}>
                        <BaseCol xs={24} sm={12} lg={6}>
                          <BaseButtonsForm.Item name="from_date" label="From Date" initialValue={null}>
                            <DatePicker disabled={!editable} />
                          </BaseButtonsForm.Item>
                        </BaseCol>
                        <BaseCol xs={24} sm={12} lg={6}>
                          <BaseButtonsForm.Item name="to_date" label="To Date" initialValue={null}>
                            <DatePicker disabled={!editable} />
                          </BaseButtonsForm.Item>
                        </BaseCol>
                      </BaseRow>
                    </BaseCol>

                    <BaseCol xs={24} md={12}>
                      <BaseRow gutter={[16, 0]}>
                        <BaseCol xs={24} sm={12} lg={6}>
                          <BaseButtonsForm.Item name="lead_time" label="Lead Time" initialValue={0}>
                            <InputNumber disabled={!editable} />
                          </BaseButtonsForm.Item>
                        </BaseCol>
                        <BaseCol xs={24} sm={12} lg={6}>
                          <BaseButtonsForm.Item
                            name="lead_time_measure"
                            label=" "
                            initialValue="days"
                            tooltip="Lead time sets the time or dates customer is allowed to reserve the appointable product. This will override the min max duration and from to duration configurations. If no lead time set default to 0."
                            rules={[
                              { required: form.getFieldValue('lead_time') > 0, message: 'Lead time unit is required' },
                            ]}
                          >
                            <Select
                              disabled={!editable}
                              placeholder="Select Lead Time Unit"
                              getPopupContainer={getPopupContainer}
                            >
                              {leadTimeUnitOptions?.map((option) => (
                                <Option key={option.name} value={option.key}>
                                  {option.name}
                                </Option>
                              ))}
                            </Select>
                          </BaseButtonsForm.Item>
                        </BaseCol>
                      </BaseRow>
                    </BaseCol>

                    <BaseCol xs={24} md={12}>
                      <BaseRow gutter={[16, 0]}>
                        <BaseCol xs={24} sm={12} lg={6}>
                          <BaseButtonsForm.Item name="from_time" label="From Time">
                            <TimePicker disabled={!editable} />
                          </BaseButtonsForm.Item>
                        </BaseCol>
                        <BaseCol xs={24} sm={12} lg={6}>
                          <BaseButtonsForm.Item name="to_time" label="To Time">
                            <TimePicker disabled={!editable} />
                          </BaseButtonsForm.Item>
                        </BaseCol>
                      </BaseRow>
                    </BaseCol>
                    <BaseCol xs={24} md={12} lg={12}>
                      <BaseButtonsForm.Item
                        name="start_time_interval"
                        label="Start time interval (in minutes)"
                        initialValue={60}
                      >
                        <InputNumber min={0} disabled={!editable} />
                      </BaseButtonsForm.Item>
                    </BaseCol>
                  </BaseRow>

                  <br />
                  <BaseCol xs={24} md={12}>
                    <BaseButtonsForm.Item
                      name="requires_confirmation"
                      valuePropName="checked"
                      label="Requires confirmation?"
                    >
                      <Checkbox disabled={!editable}>
                        <p style={{ fontSize: 13 }}>
                          Check this box if appointment requires confirmation. Payment will not be taken during
                          checkout.
                        </p>
                      </Checkbox>
                    </BaseButtonsForm.Item>
                  </BaseCol>
                </>
              )}

              {/* <BaseCol xs={24} md={24}>
                <BaseButtonsForm.Item name="custom_pricing_rules" valuePropName="checked" label="Custom pricing rules?">
                  <Checkbox>
                    <p style={{ fontSize: 13 }}>Check this box if the appointment has custom pricing rules.</p>
                  </Checkbox>
                </BaseButtonsForm.Item>
              </BaseCol>

              <BaseCol xs={24} md={12}>
                <BaseButtonsForm.Item name="can_be_cancelled" valuePropName="checked" label="Can be cancelled?">
                  <Checkbox>
                    <p style={{ fontSize: 13 }}>
                      Check this box if appointment can be cancelled by the customer. A refund will not be sent
                      automatically.
                    </p>
                  </Checkbox>
                </BaseButtonsForm.Item>
              </BaseCol>

              <BaseCol xs={24} md={12}>
                <BaseButtonsForm.Item name="can_be_rescheduled" valuePropName="checked" label="Can be rescheduled?">
                  <Checkbox>
                    <p style={{ fontSize: 13 }}>Check this box if appointment can be rescheduled by the customer.</p>
                  </Checkbox>
                </BaseButtonsForm.Item>
              </BaseCol>

              <BaseCol xs={24} md={12}>
                <BaseButtonsForm.Item name="customer_timezone" valuePropName="checked" label="Customer timezones?">
                  <Checkbox>
                    <p style={{ fontSize: 13 }}>Check this box if can be converted to customer&#39;s timezone.</p>
                  </Checkbox>
                </BaseButtonsForm.Item>
              </BaseCol> */}
              {productType === 'AP' && (
                <BaseRow gutter={[16, 0]}>
                  <BaseCol xs={24} md={12} lg={8}>
                    <BaseButtonsForm.Item
                      name="calendar_color"
                      label="Calendar Color"
                      rules={[{ required: productType === 'AP', message: 'Calendar color is required' }]}
                    >
                      <BaseInput
                        type="color"
                        ref={calendarColor}
                        style={{ width: '80%' }}
                        onChange={handleCalendarColor}
                        defaultValue={'#0073aa'}
                        disabled={!editable}
                      />
                      <p style={{ fontSize: 13, marginTop: 10 }}>
                        Pick a color that will represent this appointable product inside admin calendar.
                      </p>
                    </BaseButtonsForm.Item>
                  </BaseCol>
                </BaseRow>
              )}
              <BaseRow gutter={[16, 16]}>
                <BaseCol xs={24} md={8} lg={12}>
                  <BaseButtonsForm.Item name="description" label="Product Description" key="description">
                    <TextArea
                      placeholder={t('Enter product Description')}
                      disabled={!editable}
                      rows={4}
                      onChange={(e) => e.target.value}
                    />
                  </BaseButtonsForm.Item>
                </BaseCol>
              </BaseRow>
              <BaseRow gutter={[16, 16]}>
                <BaseCol xs={24} md={8} lg={12}>
                  <BaseButtonsForm.Item name="status" label="Status">
                    <BaseSelect placeholder="Select status" disabled={!editable}>
                      {statusOptions?.map((option) => (
                        <Option key={option.name} value={option.key}>
                          {option.name}
                        </Option>
                      ))}
                    </BaseSelect>
                  </BaseButtonsForm.Item>

                  <p style={{ fontSize: '12px', color: '#888' }}>
                    The description is not prominent by default; however, some themes may show it.
                  </p>
                </BaseCol>
              </BaseRow>
            </TabPane>

            <TabPane
              tab={
                <span>
                  <ShoppingCartOutlined />
                  {t('Inventory')}
                </span>
              }
              key="2"
            >
              {/* Form for Tab 2 */}
              <BaseRow gutter={[16, 16]}>
                <BaseCol xs={24} md={12}>
                  <BaseButtonsForm.Item
                    name="sku"
                    label="SKU"
                    key="sku"
                    rules={[{ required: true, message: 'SKU is required' }]}
                  >
                    <BaseInput placeholder="Enter SKU" disabled={!editable}/>
                  </BaseButtonsForm.Item>
                </BaseCol>
                <BaseCol xs={24} md={12}>
                  <BaseButtonsForm.Item name="quantity" label="Quantity" key="quantity">
                    <InputNumber placeholder="Enter Quantity" min={0} disabled={!editable}
                     style={{ width: '100%' }} />
                  </BaseButtonsForm.Item>
                </BaseCol>
                <BaseCol xs={24} md={24}>
                  <BaseButtonsForm.Item name="show_quantity_field">
                    <Checkbox
                      checked={showQuantityLabel}
                      onChange={(e) => setShowQuantityLabel(e?.target?.checked)}
                      disabled={!editable}
                    >
                      Show quantity field in listing?
                    </Checkbox>
                  </BaseButtonsForm.Item>
                </BaseCol>

                {showQuantityLabel == true && (
                  <>
                    <BaseCol xs={24} md={24}>
                      <BaseButtonsForm.Item name="quantity_label" label="Label of quantity field" key="quantity_label">
                        <BaseInput placeholder="Enter Quantity Label" disabled={!editable} />
                      </BaseButtonsForm.Item>
                    </BaseCol>
                  </>
                )}
              </BaseRow>
            </TabPane>

            <TabPane
              tab={
                <span>
                  <ProfileOutlined />
                  {t('Product Images')}
                </span>
              }
              key="3"
            >
              <BaseRow gutter={[16, 16]}>
                <BaseCol xs={24} md={24} lg={24}>
                  <p style={{ fontStyle: 'italic', fontSize: '14px' }}>
                    *Please make sure to upload an image with 450 x 450 dimensions
                  </p>
                  <Assets
                    type="images"
                    code="product"
                    targetFiles={setProductImages}
                    editable={editable}
                    previouslySelectedFiles={productImages}
                  />
                </BaseCol>
              </BaseRow>
            </TabPane>
            {showZoomLinkTab && (
              <>
                <TabPane
                  tab={
                    <span>
                      <VideoCameraOutlined />
                      {t('Zoom Links')}
                    </span>
                  }
                  key="4"
                >
                  <ZoomLinks
                    allZoomLinks={allZoomLinks}
                    type={type}
                    editable={editable}
                    form={form}
                    selectedZoomLinks={selectedZoomLinks}
                    setSelectedZoomLinks={setSelectedZoomLinks}
                  />
                </TabPane>
              </>
            )}
            {showPricingOptionsTab && (
              <TabPane
                tab={
                  <span>
                    <DollarOutlined />
                    {t('Pricing Options')}
                  </span>
                }
                key="5"
              >
                <PricingOptionsSection
                  type={type}
                  productPricingOptions={productPricingOptions}
                  setProductPricingOptions={setProductPricingOptions}
                  pricingOptionMeasures={pricingOptionMeasures}
                />
              </TabPane>
            )}
          </Tabs>
          <br />
          <br />
          <br></br>
          <BaseButtonsForm.Item>
            <BaseButton type="primary" htmlType="submit" loading={isLoading} disabled={!editable}>
              {t('Save Product')}
            </BaseButton>
          </BaseButtonsForm.Item>
        </BaseButtonsForm>
      </BaseCard>
    </>
  );
};

export default ProductForm;
